// ProjectUserMapping.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Trash2, Edit, X, Search, Download, Upload, Filter } from 'lucide-react';
import ReusableTable from '../dashboard/ReusableTable';
import ReusableForm from '../common/ReusableForm';
import ReusableDeleteDialog from '../common/ReusableDeleteDialog';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Dropdown } from 'primereact/dropdown'; // Keep this for filter dropdowns
import { Calendar } from 'primereact/calendar';
import { getRequest, postRequest } from '../services/apiService';

const ProjectUserMapping = () => {
  const toast = useRef(null);
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dropdown data states
  const [projects, setProjects] = useState([]);
  const [ships, setShips] = useState([]);
  const [users, setUsers] = useState([]);
  const [taskLevels, setTaskLevels] = useState([[]]); // Array of arrays for tasks at different levels
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingShips, setLoadingShips] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingTasks, setLoadingTasks] = useState(false);

  // Filter states
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedShip, setSelectedShip] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);

  const [formData, setFormData] = useState({
    id: null,
    project: '',
    ship: '',
    user: '',
    tasks: [], // Array to hold selected tasks at each level
    startDate: null,
    endDate: null
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [mappingToDelete, setMappingToDelete] = useState(null);
  const [deleteFormData, setDeleteFormData] = useState({ confirmation: "" });
  const [search, setSearch] = useState("");
  const [sameDates, setSameDates] = useState(false);

  // Cache and abort controllers
  const abortControllerRef = useRef(null);
  const taskCache = useRef({});
  const shipCache = useRef({});

  useEffect(() => {
    fetchUsers();
    fetchProjects();

    // Cleanup on unmount
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    fetchMappings();
  }, [selectedShip, selectedTask]);

  const fetchMappings = async () => {
    try {
      setLoading(true);
      let url = 'pert/api/task/assign/';

      const params = new URLSearchParams();
      if (selectedShip) params.append('project_id', selectedShip);
      if (selectedTask) params.append('task_id', selectedTask);

      if (params.toString()) url += `?${params.toString()}`;

      const response = await getRequest(url);

      if (response.error) {
        setMappings([]);
        showNotification(response.error, 'info');
      } else if (Array.isArray(response) && response.length > 0) {
        setMappings(response.map(item => ({
          id: item.id,
          task_id: item.task_id,
          task_name: item.task_name,
          assigned_to: item.assigned_to,
          assigned_to_fullname: item.assigned_to_fullname,
          ship_id: item.ship_id,
          project_name: item.parent?.name || 'N/A',
          assigned_by_fullname: item.assigned_by_fullname,
          parent: item.parent,
          start_date: item.start_date ? new Date(item.start_date) : null,
          end_date: item.end_date ? new Date(item.end_date) : null
        })));
      } else {
        setMappings([]);
        showNotification('No assignments found for the given criteria', 'info');
      }
    } catch (error) {
      showNotification('Failed to fetch mappings', 'error');
      setMappings([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchTasks = useCallback(async (shipId, parentTaskId = null, level = 0) => {
    try {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const controller = new AbortController();
      abortControllerRef.current = controller;

      setLoadingTasks(true);

      const cacheKey = `${shipId}-${parentTaskId}`;
      if (taskCache.current[cacheKey]) {
        setTaskLevels(prev => {
          const newLevels = [...prev];
          newLevels[level] = taskCache.current[cacheKey];
          return newLevels;
        });
        return taskCache.current[cacheKey];
      }

      if (!taskCache.current[cacheKey]) {
        setTaskLevels(prev => {
          const newLevels = [...prev];
          newLevels[level] = [];
          return newLevels;
        });
      }

      let url = `pert/task-transaction-api/?project_id=${shipId}`;
      if (parentTaskId !== null) {
        url += `&parent_task_id=${parentTaskId}`;
      } else {
        url += `&parent_task_id=null`; // For top-level tasks
      }

      const response = await getRequest(
        url,
        { signal: controller.signal }
      );

      if (controller.signal.aborted) return;

      if (response?.data && Array.isArray(response.data) && response.data.length > 0) {
        const tasksData = response.data.map(task => ({
          label: task.name || `Task ${task.id}`,
          value: task.id,
          type: task.task_type,
          status: task.is_completed ? 'Completed' : 'Pending'
        }));

        taskCache.current[cacheKey] = tasksData;
        setTaskLevels(prev => {
          const newLevels = [...prev];
          newLevels[level] = tasksData;
          // Clear subsequent levels if new tasks are fetched at a higher level
          for (let i = level + 1; i < newLevels.length; i++) {
            newLevels[i] = [];
          }
          return newLevels;
        });

        if (isEditing && formData.tasks[level] && !tasksData.some(t => t.value === formData.tasks[level])) {
          setFormData(prev => {
            const newTasks = [...prev.tasks];
            newTasks[level] = '';
            return { ...prev, tasks: newTasks };
          });
          showNotification('Previously assigned task is no longer available', 'warning');
        }
        return tasksData;
      } else {
        setTaskLevels(prev => {
          const newLevels = [...prev];
          newLevels[level] = [];
          // If no tasks at this level, clear all subsequent levels
          for (let i = level + 1; i < newLevels.length; i++) {
            newLevels[i] = [];
          }
          return newLevels;
        });
        taskCache.current[cacheKey] = [];
        return [];
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        showNotification('Failed to fetch tasks', 'error');
      }
      return [];
    } finally {
      if (!abortControllerRef.current?.signal.aborted) {
        setLoadingTasks(false);
        abortControllerRef.current = null;
      }
    }
  }, [isEditing, formData.tasks]);

  const fetchProjects = async () => {
    try {
      setLoadingProjects(true);
      const response = await getRequest('master/projects/');
      if (response.status === 'success') {
        const projectsData = response.data.map(project => ({
          label: project.name,
          value: project.id,
        }));
        setProjects(projectsData);
        return projectsData;
      }
    } catch (error) {
      showNotification('Failed to fetch projects', 'error');
      return [];
    } finally {
      setLoadingProjects(false);
    }
  };

  const fetchShips = useCallback(async (projectId) => {
    try {
      if (shipCache.current[projectId]) {
        setShips(shipCache.current[projectId]);
        return shipCache.current[projectId];
      }

      setLoadingShips(true);

      const response = await getRequest(`master/projects/?parent_id=${projectId}`);
      if (response.status === 'success') {
        const shipsData = response.data.map(ship => ({
          label: ship.name,
          value: ship.id,
          code: ship.code,
          shipyard: ship.shipyard?.name || 'N/A'
        }));

        shipCache.current[projectId] = shipsData;
        setShips(shipsData);

        if (isEditing && formData.ship) {
          const shipExists = shipsData.some(s => s.value === formData.ship);
          if (!shipExists) {
            setFormData(prev => ({ ...prev, ship: '' }));
          }
        }
        return shipsData;
      }
      return [];
    } catch (error) {
      showNotification('Failed to fetch ships', 'error');
      setShips([]);
      return [];
    } finally {
      setLoadingShips(false);
    }
  }, [isEditing, formData.ship]);

  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      const response = await getRequest('/config/checkedinusers/');
      if (Array.isArray(response)) {
        setUsers(response.map(user => ({
          label: `${user.first_name} ${user.last_name} (${user.loginname})`,
          value: user.id,
          email: user.email,
          unit: user.unit?.name || 'N/A',
          rank: user.rankName || 'N/A'
        })));
      } else {
        setUsers([]);
      }
    } catch (error) {
      showNotification('Failed to fetch users', 'error');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleProjectFilterChange = (projectId) => {
    setSelectedProject(projectId);
    setSelectedShip(null);
    if (projectId) {
      fetchShips(projectId);
    } else {
      setShips([]);
    }
  };

  const handleProjectChange = (projectId) => {
    setFormData(prev => ({
      ...prev,
      project: projectId,
      ship: '',
      tasks: [] // Clear tasks when project changes
    }));
    setTaskLevels([[]]); // Reset task levels
    if (projectId) {
      fetchShips(projectId);
    } else {
      setShips([]);
      setTaskLevels([[]]);
    }
  };

  const handleShipChange = async (shipId) => {
    setFormData(prev => ({
      ...prev,
      ship: shipId,
      tasks: [] // Clear tasks when ship changes
    }));
    setTaskLevels([[]]); // Reset task levels

    if (shipId) {
      const initialTasks = await fetchTasks(shipId, null, 0); // Fetch top-level tasks
      if (initialTasks.length > 0) {
        setTaskLevels([initialTasks]);
      }
    } else {
      setTaskLevels([[]]);
    }
  };

  const handleTaskChange = async (value, level) => {
    setFormData(prev => {
      const newTasks = [...prev.tasks];
      newTasks[level] = value;
      // Clear subsequent task selections
      for (let i = level + 1; i < newTasks.length; i++) {
        newTasks[i] = '';
      }
      return { ...prev, tasks: newTasks };
    });

    if (formData.ship && value) {
      // Fetch subtasks for the selected task
      const subtasks = await fetchTasks(formData.ship, value, level + 1);
      if (subtasks.length > 0) {
        setTaskLevels(prev => {
          const newLevels = [...prev];
          newLevels[level + 1] = subtasks;
          // Ensure we don't have empty arrays after valid levels
          for (let i = level + 2; i < newLevels.length; i++) {
            newLevels[i] = [];
          }
          return newLevels;
        });
      } else {
        // If no subtasks, remove subsequent levels
        setTaskLevels(prev => prev.slice(0, level + 1));
      }
    } else {
      // If task is unselected or ship is not selected, remove subsequent levels
      setTaskLevels(prev => prev.slice(0, level + 1));
    }
  };


  const showNotification = (message, type) => {
    toast.current?.show({
      severity: type,
      summary: type.charAt(0).toUpperCase() + type.slice(1),
      detail: message,
      life: 3000
    });
  };

  const resetForm = () => {
    setFormData({
      id: null,
      project: '',
      ship: '',
      user: '',
      tasks: [],
      startDate: null,
      endDate: null
    });
    setTaskLevels([[]]); // Reset task levels
    setIsEditing(false);
    setSameDates(false);
  };

  const handleSameDatesToggle = () => {
    setSameDates(prev => !prev);
    if (!sameDates && formData.startDate) {
      setFormData(prev => ({ ...prev, endDate: prev.startDate }));
    }
  };

  const handleSubmit = async () => {
    try {
      // Use the last selected task from the tasks array
      const finalTaskId = formData.tasks[formData.tasks.length - 1];

      const payload = {
        task_id: finalTaskId,
        assigned_to_id: formData.user,
        project_id: formData.ship,
        start_date: formData.startDate ? formData.startDate.toISOString().split('T')[0] : null,
        end_date: formData.endDate ? formData.endDate.toISOString().split('T')[0] : null
      };

      if (isEditing) {
        payload.id = formData.id;
        await postRequest('pert/api/task/assign/', payload);
        showNotification('Assignment updated successfully', 'success');
      } else {
        await postRequest('pert/api/task/assign/', payload);
        showNotification('Assignment added successfully', 'success');
      }

      fetchMappings();
    } catch (e) {
      showNotification(e.message || 'Failed to save assignment', 'error');
    }
    resetForm();
    setIsModalOpen(false);
  };

  const handleEdit = async (mapping) => {
    console.log("Editing mapping:", mapping);
    try {
      setIsEditing(true);
      setIsModalOpen(true);

      const projectId = mapping.parent?.id;
      const shipId = mapping.ship_id;
      const userId = mapping.assigned_to;
      const taskId = mapping.task_id;
      const startDate = mapping.start_date;
      const endDate = mapping.end_date;

      await fetchProjects(); // Ensure projects are loaded
      if (projectId) {
        await fetchShips(projectId); // Ensure ships are loaded for the project
      }
      await fetchUsers(); // Ensure users are loaded

      // For tasks, we need to re-fetch and set all parent tasks up to the selected one
      // This is a more complex part for editing nested dropdowns.
      // For simplicity, let's assume we load the immediate parent and the task itself for now.
      // A more robust solution would involve recursively fetching parents.

      // Start by fetching the top-level tasks for the ship
      if (shipId) {
        const topLevelTasks = await fetchTasks(shipId, null, 0);
        if (topLevelTasks.length > 0) {
          setTaskLevels([topLevelTasks]);

          // Now, try to find the path to the current taskId
          let currentParentTask = null;
          let currentTasksArray = topLevelTasks;
          let foundPath = [];

          const findTaskPath = async (tasks, targetId, currentPath, currentLevel) => {
            for (const task of tasks) {
              if (task.value === targetId) {
                foundPath.push(task.value);
                return true;
              }
              const subtasks = await fetchTasks(shipId, task.value, currentLevel + 1);
              if (subtasks.length > 0) {
                setTaskLevels(prev => {
                  const newLevels = [...prev];
                  newLevels[currentLevel + 1] = subtasks;
                  return newLevels;
                });
                if (await findTaskPath(subtasks, targetId, [...currentPath, task.value], currentLevel + 1)) {
                  foundPath.unshift(task.value); // Add parent to the beginning of the path
                  return true;
                }
              }
            }
            return false;
          };

          await findTaskPath(topLevelTasks, taskId, [], 0);

          if (foundPath.length > 0) {
            setFormData(prev => ({
              ...prev,
              id: mapping.id,
              project: projectId,
              ship: shipId,
              user: userId,
              tasks: foundPath, // Set the array of selected tasks
              startDate: startDate,
              endDate: endDate
            }));
          } else {
            // Fallback if task path not found, clear tasks
            setFormData(prev => ({
              ...prev,
              id: mapping.id,
              project: projectId,
              ship: shipId,
              user: userId,
              tasks: [],
              startDate: startDate,
              endDate: endDate
            }));
            showNotification('Assigned task could not be loaded, please re-select.', 'warning');
          }
        }
      }


      setFormData(prev => ({
        ...prev,
        id: mapping.id,
        project: projectId,
        ship: shipId,
        user: userId,
        // task: taskId, // This will be handled by tasks array
        startDate: startDate,
        endDate: endDate
      }));

      if (startDate && endDate && startDate.getTime() === endDate.getTime()) {
        setSameDates(true);
      } else {
        setSameDates(false);
      }

      console.log("Form data after setting:", {
        id: mapping.id,
        project: projectId,
        ship: shipId,
        user: userId,
        tasks: formData.tasks, // Check this after path finding
        startDate: startDate,
        endDate: endDate
      });

    } catch (error) {
      showNotification('Failed to load edit data', 'error');
      console.error("Error in handleEdit:", error);
    }
  };


  const fetchProjectByShipId = async (shipId) => {
    try {
      const response = await getRequest(`master/projects/${shipId}/`);
      if (response.status === 'success') {
        const projectId = response.data.parent_id;
        if (projectId) {
          setFormData(prev => ({ ...prev, project: projectId }));
          fetchShips(projectId);
        }
      }
    } catch (error) {
      showNotification('Failed to fetch project for this ship', 'error');
    }
  };

  const openDeleteModal = (mapping) => {
    setMappingToDelete(mapping);
    setDeleteFormData({ confirmation: '' });
    setShowDeleteDialog(true);
  };

  const confirmDelete = async (e) => {
    e.preventDefault();
    if (deleteFormData.confirmation !== 'DELETE') {
      showNotification('Please type DELETE to confirm', 'error');
      return;
    }

    try {
      await postRequest('pert/api/task/assign/', {
        id: mappingToDelete.id,
        status_db: 3
      });

      fetchMappings();
      showNotification('Assignment deleted successfully', 'warning');
    } catch (error) {
      showNotification('Failed to delete assignment', 'error');
    }
    setShowDeleteDialog(false);
  };

  // Custom item template for the task dropdown options (in the list)
  const taskItemTemplate = (option) => {
    if (!option) {
      return null; // or empty string/element as appropriate for null option
    }
    const words = option.label.split(' ');
    if (words.length > 10) {
      return (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
          {option.label}
        </div>
      );
    }
    return <span>{option.label}</span>;
  };

  // Custom selected item template for the task dropdown (in the input field after selection)
  const taskSelectedItemTemplate = (option, props) => {
    if (option) {
      const words = option.label.split(' ');
      if (words.length > 10) {
        return (
          <div style={{ whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
            {option.label}
          </div>
        );
      }
      return <div>{option.label}</div>;
    }
    return <span>{props.placeholder}</span>;
  };


  // Form configuration for ReusableForm
  const formFields = [
    {
      name: 'project',
      label: 'Project',
      type: 'select',
      required: true,
      options: projects,
      placeholder: loadingProjects ? "Loading..." : "Select a Project",
      disabled: loadingProjects || isEditing,
      onChange: (e) => handleProjectChange(e.value)
    },
    {
      name: 'ship',
      label: 'Ship',
      type: 'select',
      required: true,
      options: ships,
      placeholder: "Select a Ship",
      disabled: !formData.project || loadingShips,
      onChange: (e) => handleShipChange(e.value)
    },
    ...taskLevels.map((tasksAtLevel, level) => ({
      name: `task-${level}`, // Unique name for each task dropdown
      label: level === 0 ? 'Task/Milestone' : `Subtask Level ${level}`,
      type: 'select',
      required: true,
      options: tasksAtLevel,
      placeholder: loadingTasks ? "Loading..."
        : tasksAtLevel.length > 0 ? `Select ${level === 0 ? 'Task/Milestone' : 'Subtask'}`
          : (level === 0 && !formData.ship) ? "Select ship first"
            : (level > 0 && !formData.tasks[level - 1]) ? "Select parent task first"
              : "No tasks available",
      disabled: !formData.ship || (level > 0 && !formData.tasks[level - 1]) || loadingTasks,
      value: formData.tasks[level] || '', // Bind value from formData.tasks array
      onChange: (e) => handleTaskChange(e.value, level),
      filter: true,
      itemTemplate: taskItemTemplate,
      selectedItemTemplate: taskSelectedItemTemplate,
      virtualScrollerOptions: { itemSize: 38 },
      className: 'mt-2'
    })),
    {
      name: 'user',
      label: 'User',
      type: 'select',
      required: true,
      options: users,
      placeholder: "Select User",
      disabled: loadingUsers,
      onChange: (e) => setFormData(prev => ({ ...prev, user: e.value }))
    },
    {
      name: 'startDate',
      label: 'Start Date',
      type: 'calendar',
      required: true,
      value: formData.startDate,
      onChange: (e) => {
        setFormData(prev => ({ ...prev, startDate: e.value }));
        if (sameDates) {
          setFormData(prev => ({ ...prev, endDate: e.value }));
        }
      },
      dateFormat: 'dd/mm/yy'
    },
    {
      name: 'endDate',
      label: 'End Date',
      type: 'calendar',
      required: true,
      value: formData.endDate,
      onChange: (e) => setFormData(prev => ({ ...prev, endDate: e.value })),
      dateFormat: 'dd/mm/yy',
      disabled: sameDates
    },
    {
      name: 'sameDatesToggle',
      label: 'Same Start and End Date',
      type: 'checkbox',
      checked: sameDates,
      onChange: handleSameDatesToggle,
      className: 'mt-2'
    }
  ];

  const columnFields = [
    { field: 'assigned_to_fullname', header: 'Assigned To' },
    { field: 'project_name', header: 'Project Name' },
    { field: 'task_name', header: 'Task Name' },
    { field: 'assigned_by_fullname', header: 'Assigned By' },
    { field: 'start_date', header: 'Start Date', body: (rowData) => rowData.start_date ? rowData.start_date.toLocaleDateString('en-GB') : 'N/A' },
    { field: 'end_date', header: 'End Date', body: (rowData) => rowData.end_date ? rowData.end_date.toLocaleDateString('en-GB') : 'N/A' }
  ];

  const actionItems = [
    { label: 'Edit', icon: 'pi pi-pencil', command: handleEdit },
    { label: 'Delete', icon: 'pi pi-trash', command: openDeleteModal }
  ];

  const deleteFormFields = [
    {
      name: "confirmation",
      label: "Confirm Deletion",
      type: "text",
      required: true,
      placeholder: "Type 'DELETE' to confirm",
      helpText: "This action cannot be undone. Type 'DELETE' to confirm deletion."
    }
  ];

  return (
    <div className="bg-gray-50 min-h-screen p-6 pt-0">
      <Toast ref={toast} position="top-right" />

      {/* Filter Section */}
      <div className="bg-white rounded-lg shadow-sm mb-4 p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="field">
            <label htmlFor="filter-project" className="block text-sm font-medium text-gray-700 mb-2">
              Filter by Project
            </label>
            <Dropdown
              id="filter-project"
              value={selectedProject}
              onChange={(e) => handleProjectFilterChange(e.value)}
              options={projects}
              optionLabel="label"
              optionValue="value"
              placeholder="Select Project"
              filter
              loading={loadingProjects}
              className="w-full"
            />
          </div>

          <div className="field">
            <label htmlFor="filter-ship" className="block text-sm font-medium text-gray-700 mb-2">
              Filter by Ship
            </label>
            <Dropdown
              id="filter-ship"
              value={selectedShip}
              onChange={(e) => setSelectedShip(e.value)}
              options={ships}
              optionLabel="label"
              optionValue="value"
              placeholder="Select Ship"
              filter
              loading={loadingShips}
              className="w-full"
              disabled={!selectedProject || loadingShips}
            />
          </div>

          <div className="field">
            <label htmlFor="filter-task" className="block text-sm font-medium text-gray-700 mb-2">
              Filter by Task
            </label>
            {loadingTasks ? (
              <div className="p-2 text-sm text-gray-500 border rounded flex items-center">
                <i className="pi pi-spinner pi-spin mr-2"></i> Loading tasks...
              </div>
            ) : taskLevels[0]?.length > 0 ? ( // Use taskLevels[0] for filter task dropdown
              <Dropdown
                id="filter-task"
                value={selectedTask}
                onChange={(e) => setSelectedTask(e.value)}
                options={taskLevels[0]} // Use the top-level tasks for filtering
                optionLabel="label"
                optionValue="value"
                placeholder="Select Task"
                filter
                className="w-full"
                disabled={!selectedShip}
                virtualScrollerOptions={{ itemSize: 38 }}
              />
            ) : (
              <div className="p-2 text-sm text-gray-500 border rounded">
                {selectedShip ? "No tasks available" : "Select a ship first"}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="bg-white rounded-lg shadow-sm">
        <ReusableTable
          title="User To Project Mapping"
          data={mappings}
          columnFields={columnFields}
          actionMenu
          actionItems={actionItems}
          searchValue={search}
          onSearchChange={setSearch}
          setAddNewDialogVisible={setIsModalOpen}
          loading={loading}
        />
        {loading && mappings.length === 0 && (
          <div className="p-8 text-center">
            <div className="flex items-center justify-center">
              <i className="pi pi-spinner pi-spin mr-2"></i>
              Loading assignments...
            </div>
          </div>
        )}
        {!loading && mappings.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            No assignments found for the selected criteria.
          </div>
        )}
      </div>

      {/* Form Modal Section - ReusableForm Outside Dialog */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full mx-auto max-w-2xl animate-fade-in">
            <ReusableForm
              formData={formData}
              setFormData={setFormData}
              fields={formFields}
              onSubmit={handleSubmit}
              onHide={() => {
                setIsModalOpen(false);
                resetForm();
              }}
              submitLabel={isEditing ? 'Update' : 'Save'}
              headerText={isEditing ? 'Edit Mapping' : 'Add New Mapping'}
              hideCancelButton={true}
            />
          </div>
        </div>
      )}

      {/* Delete Dialog */}
      <ReusableDeleteDialog
        visible={showDeleteDialog}
        onHide={() => {
          setShowDeleteDialog(false);
          setDeleteFormData({ confirmation: "" });
        }}
        onDelete={confirmDelete}
        fields={deleteFormFields}
        formData={deleteFormData}
        setFormData={setDeleteFormData}
        title="Delete Assignment"
        description={mappingToDelete && `You are about to delete assignment for "${mappingToDelete.assigned_to_fullname}" on task "${mappingToDelete.task_name}".`}
        deleteLabel="Delete Assignment"
      />
    </div>
  );
};

export default ProjectUserMapping;